import { useLingui } from '@lingui/react/macro';
import { DocumentText, SearchNormal1 } from 'iconsax-reactjs';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Breadcrumb } from '@/base/Breadcrumb';
import { SEO_ORIGIN } from '@/base/constants';
import { PageMeta } from '@/base/PageMeta';
import { Link } from '@/base/router';
import { LandingLayout } from '@/landing/LandingLayout';
import { useSettings } from '@/settings/api';
import { Input } from '@/shared/Input';
import { cn, useLazyValue } from '@/shared/react';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { Widget } from '@/shared/Widget';
import { useDocsIndex } from './useDocsIndex';

export const GuidePage: FC = () => {
  const { t } = useLingui();
  const [{ language }] = useSettings();
  const [search, setSearch] = useState('');
  const debouncedSearch = useLazyValue(search, 300);
  const entries = useDocsIndex('guide');
  usePupeteerSignal(true);

  const groups = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    const filtered = query
      ? entries.filter((entry) =>
          `${entry.title} ${entry.description ?? ''}`
            .toLowerCase()
            .includes(query),
        )
      : entries;

    const map = new Map<string, typeof entries>();
    for (const entry of filtered) {
      const key = entry.section || t`More Guides`;
      map.set(key, [...(map.get(key) ?? []), entry]);
    }
    return [...map.entries()];
  }, [entries, debouncedSearch, t]);

  return (
    <LandingLayout>
      <PageMeta
        title={t`Guide`}
        description={t`Browse IQX help articles, guides, and policies — search to find what you need.`}
        localizedPath="/guide"
      />
      <Breadcrumb
        className="w-full max-w-desktop mx-auto px-3"
        items={[
          { name: t`Home`, url: `${SEO_ORIGIN}/${language}` },
          { name: t`Guide`, url: `${SEO_ORIGIN}/${language}/guide` },
        ]}
      />
      <main className="w-full max-w-desktop min-h-svh mx-auto px-3 py-8 desktop:py-12 flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 py-6 desktop:py-10">
          <h1 className="text-2xl desktop:text-4xl font-semibold">
            {t`How can we help you?`}
          </h1>
          <p className="text-base text-content-tertiary max-w-lg">
            {t`Search our guides, FAQs, and policies, or browse everything below.`}
          </p>
          <Input
            size={12}
            block
            value={search}
            onChange={(val) => setSearch(val ?? '')}
            placeholder={t`Search guides...`}
            prefix={
              <SearchNormal1 size={18} className="text-content-tertiary" />
            }
            className="max-w-lg"
          />
        </div>

        {groups.length === 0 && (
          <p className="text-center text-content-tertiary py-12">
            {t`No guides matched your search.`}
          </p>
        )}

        {groups.map(([section, items]) => (
          <section key={section} className="flex flex-col gap-4">
            <h2 className="text-lg font-medium">{section}</h2>
            <div className="grid grid-cols-1 desktop:grid-cols-3 gap-4">
              {items.map((item) => (
                <Link
                  key={item.slug}
                  to={`/${language}/${item.slug}`}
                  workspace="public"
                  className="contents"
                >
                  <Widget
                    className={cn(
                      'flex flex-col gap-2 h-full transition-colors',
                      'hover:border-border-brand hover:bg-background-brand/5',
                    )}
                  >
                    <DocumentText
                      size={22}
                      className="text-content-brand shrink-0"
                    />
                    <h3 className="font-medium">{item.title}</h3>
                    {item.description && (
                      <p className="text-sm text-content-tertiary line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </Widget>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>
    </LandingLayout>
  );
};
